/**
 * Authentication Provider Component
 *
 * Wraps the entire application with MSAL authentication context.
 * This enables all child components to access authentication state and methods.
 */

import { useEffect, useState } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress } from '@mui/material';
import { msalConfig, API_SCOPE, loginRequest } from './msalConfig';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        await msalInstance.initialize();
        // Specifically wait for the redirect promise to finish 
        // before marking the app as initialized.
        await msalInstance.handleRedirectPromise();
        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL Initialization failed:', error);
        // Still set initialized so the rest of the app can render 
        // (error handling is in the components)
        setIsInitialized(true);
      }
    };
    init();
  }, []);

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();

  /**
   * Get access token for backend API calls
   * Tries silent token acquisition first, falls back to interactive popup
   */
  const getAccessToken = async (): Promise<string> => {
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    const request = {
      scopes: [API_SCOPE],
      account: accounts[0],
    };

    try {
      // Try to get token silently (from cache or refresh token)
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      // Silent acquisition failed, use interactive method
      console.log('Silent token acquisition failed, using popup', error);

      // Prevent interaction collision
      if (inProgress !== InteractionStatus.None) {
        throw new Error('Authentication interaction already in progress. Please wait.');
      }

      const response = await instance.acquireTokenPopup(request);
      return response.accessToken;
    }
  };

  /**
   * Initiate login flow (redirect to Entra External ID)
   */
  const login = () => {
    if (inProgress !== InteractionStatus.None) {
      console.warn('Login attempt blocked: interaction already in progress');
      return;
    }
    instance.loginRedirect(loginRequest);
  };

  /**
   * Logout and clear tokens
   */
  const logout = () => {
    if (inProgress !== InteractionStatus.None) {
      console.warn('Logout attempt blocked: interaction already in progress');
      return;
    }
    instance.logoutRedirect();
  };

  return {
    isAuthenticated: accounts.length > 0,
    user: accounts[0],
    inProgress,
    getAccessToken,
    login,
    logout,
  };
};
